import React, { useState, useEffect } from "react";
import { 
  Card, 
  Button, 
  Alert, 
  Table, 
  Spinner, 
  Badge,
  ButtonGroup,
  Nav,
  Tab
} from "react-bootstrap";
import { 
  ArrowClockwise, 
  CheckCircle, 
  XCircle,
  Globe,
  Book,
  Shield,
  Eye,
  EyeSlash
} from 'react-bootstrap-icons';

// Import the API function and new components
import { getAssessmentTypes, getAssessmentScreener } from "../services/APIClient";
import SchoolScreenerSearch from "./SchoolScreenerSearch";
import CountryScreenerSearch from "./CountryScreenerSearch";
import TrialScreenerAccess from "./TrialScreenerAccess";

function ScreeningAccessManagement({ onClose }) {
  const [activeTab, setActiveTab] = useState('points');

  // Data states
  const [screeningPoints, setScreeningPoints] = useState([]);
  const [countryControls, setCountryControls] = useState([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [currentUser] = useState("admin"); // Replace with actual user context

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [points, countries] = await Promise.all([
        getAvailableScreeningPoints(), // Load global screeners for the main tab
        getCountryControls()
      ]);
      
      setScreeningPoints(points);
      setCountryControls(countries);
    } catch (err) {
      setError("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced API function using the model's grade booleans and standard flag
  const getAvailableScreeningPoints = async (countryId = null) => {
    try {
      let response;
      let isCountrySpecific = false;
      
      if (countryId) {
        console.log(`Loading screeners for country: ${countryId}`);
        
        // First try to get country-specific screeners
        response = await getAssessmentScreener({
          countryid: countryId,
          active: true,
          task: false  // Filter out task-type assessments
        });
        
        if (response && response.length > 0) {
          isCountrySpecific = true;
          console.log(`Found ${response.length} country-specific screeners for ${countryId}`);
        } else {
          return [];
        }
      }
      
      // The global list is only used when no territory was requested.
      if (!countryId) {
        response = await getAssessmentScreener({
          active: true,
          task: false
        });
        
        // If the new API returns nothing, fall back to the original API
        if (!response || response.length === 0) {
          console.log('No results from getAssessmentScreener, falling back to getAssessmentTypes');
          response = await getAssessmentTypes();
        }
      }
      
      console.log('All assessments loaded:', response);
      
      // Filter out task-type assessments (redundant check for legacy API)
      const filteredAssessments = (response || []).filter(assessment => assessment.task !== true);

      // Categorize assessments and add metadata based on the model fields
      const categorizedAssessments = filteredAssessments.map(a => {
        // Normalize grade flags from API model (snake_case)
        const isGrade1 = Boolean(a.grade_level1);
        const isGrade2 = Boolean(a.grade_level2);

        // Determine standard/custom from the model's 'standard' flag
        const isStandard = a.standard === true;
        const isCustom = !isStandard;

        // Human-readable grade label
        const gradeLevel =
          isGrade1 && isGrade2 ? 'Grade 1 & 2' :
          isGrade2 ? 'Grade 2' :
          isGrade1 ? 'Grade 1' : 'Unassigned';

        // Category for display
        let category = `${gradeLevel} ${isCustom ? 'Custom' : 'Standard'}`;
        if (isCountrySpecific && countryId) {
          category += ' (Country-Specific)';
        }

        return {
          ...a,
          category,
          gradeLevel,
          isCustom,
          isStandard,
          isGrade1,
          isGrade2,
          isCountrySpecific,
          // Respect API 'active' if present; otherwise assume active
          isActive: typeof a.active === 'boolean' ? a.active : true
        };
      });

      console.log('Categorized screening points:', categorizedAssessments);
      return categorizedAssessments;
    } catch (err) {
      console.error('Error loading screening points:', err);
      
      // Final fallback to original API
      try {
        console.log('Attempting fallback to getAssessmentTypes...');
        const fallbackResponse = await getAssessmentTypes();
        return (fallbackResponse || [])
          .filter(assessment => assessment.task !== true)
          .map(a => {
            const isGrade1 = Boolean(a.grade_level1);
            const isGrade2 = Boolean(a.grade_level2);
            const isStandard = a.standard === true;
            const isCustom = !isStandard;

            const gradeLevel =
              isGrade1 && isGrade2 ? 'Grade 1 & 2' :
              isGrade2 ? 'Grade 2' :
              isGrade1 ? 'Grade 1' : 'Unassigned';

            return {
              ...a,
              category: `${gradeLevel} ${isCustom ? 'Custom' : 'Standard'}`,
              gradeLevel,
              isCustom,
              isStandard,
              isGrade1,
              isGrade2,
              isCountrySpecific: false,
              isActive: typeof a.active === 'boolean' ? a.active : true
            };
          });
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
        throw new Error('Failed to load screening points: ' + err.message);
      }
    }
  };

  const getCountryControls = async () => {
    // No longer needed - we use search-based approach for countries too
    console.log("Country controls now handled by search component");
    return [];
  };

  const getSchoolControls = async () => {
    // No longer needed - we use search-based approach
    console.log("School controls now handled by search component");
    return [];
  };

  // Action handlers
  const toggleScreeningPoint = async (pointId, isActive) => {
    try {
      // TODO: Replace with actual API call
      console.log(`Setting screening point ${pointId} to ${isActive ? 'active' : 'inactive'}`);
      
      setScreeningPoints(prev => prev.map(point => 
        point.id === pointId ? { ...point, isActive } : point
      ));
      
      setSuccess(`Screening point ${isActive ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to toggle screening point: ${err.message}`);
    }
  };

  const toggleCountryControl = async (countryId, pointId, isEnabled) => {
    try {
      // TODO: Replace with actual API call
      console.log(`Setting country ${countryId} access to point ${pointId}: ${isEnabled ? 'enabled' : 'disabled'}`);
      
      setSuccess(`Country access ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to toggle country access: ${err.message}`);
    }
  };

  const toggleSchoolControl = async (schoolId, pointId, isEnabled) => {
    try {
      // TODO: Replace with actual API call
      console.log(`Setting school ${schoolId} access to point ${pointId}: ${isEnabled ? 'enabled' : 'disabled'}`);
      
      setSuccess(`School access ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to toggle school access: ${err.message}`);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive 
      ? <Badge bg="success"><CheckCircle className="me-1" />Active</Badge>
      : <Badge bg="secondary"><XCircle className="me-1" />Inactive</Badge>;
  };

  return (
    <div>
      {/* Main Navigation */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'points'}
            onClick={() => setActiveTab('points')}
          >
            <Shield className="me-2" />
            Screening Points
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeTab === 'trial'}
            onClick={() => setActiveTab('trial')}
          >
            <Shield className="me-2" />
            Trial Screener Access
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'country'}
            onClick={() => setActiveTab('country')}
          >
            <Globe className="me-2" />
            Country Control
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'school'}
            onClick={() => setActiveTab('school')}
          >
            <Book className="me-2" />
            School Control
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Messages */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <strong>Error:</strong> {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <strong>Success:</strong> {success}
        </Alert>
      )}

      {/* Loading Spinner */}
      {loading && (
        <div className="text-center py-4">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Tab Content */}
      {!loading && (
        <Tab.Content>
          {/* Screening Points Tab */}
          {activeTab === 'points' && (
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Available Screening Points ({screeningPoints.length})</span>
                  <div>
                    <Button variant="outline-secondary" size="sm" onClick={loadInitialData}>
                      <ArrowClockwise className="me-1" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {screeningPoints.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    {loading ? "Loading screening points..." : "No screening points available."}
                  </div>
                ) : (
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Screening Point</th>
                        <th>Grade Level</th>
                        <th>Category</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {screeningPoints.map(point => (
                        <tr key={point.id}>
                          <td>
                            <div>
                              <strong>{point.name}</strong>
                              {point.description && (
                                <>
                                  <br />
                                  <small className="text-muted">{point.description}</small>
                                </>
                              )}
                              {point.isCountrySpecific && (
                                <>
                                  <br />
                                  <Badge bg="info" className="mt-1">Country-Specific</Badge>
                                </>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge bg={
                              point.isGrade1 && point.isGrade2 ? "warning" :
                              point.isGrade2 ? "info" : 
                              point.isGrade1 ? "primary" : "secondary"
                            }>
                              {point.gradeLevel}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="secondary">{point.category}</Badge>
                          </td>
                          <td>
                            <Badge bg={point.isCustom ? "warning" : "success"}>
                              {point.isCustom ? "Custom" : "Standard"}
                            </Badge>
                          </td>
                          <td>{getStatusBadge(point.isActive)}</td>
                          <td>
                            <ButtonGroup size="sm">
                              <Button 
                                variant={point.isActive ? "outline-danger" : "outline-success"}
                                onClick={() => toggleScreeningPoint(point.id, !point.isActive)}
                                title={point.isActive ? "Disable globally" : "Enable globally"}
                              >
                                {point.isActive ? <EyeSlash /> : <Eye />}
                              </Button>
                            </ButtonGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Country Control Tab */}
          {activeTab === 'country' && (
            <CountryScreenerSearch
              availableScreeningPoints={screeningPoints}
              currentUser={currentUser}
              getAvailableScreeningPoints={getAvailableScreeningPoints}
            />
          )}

          {activeTab === 'trial' && <TrialScreenerAccess />}

          {/* School Control Tab */}
          {activeTab === 'school' && (
            <SchoolScreenerSearch
              availableScreeningPoints={screeningPoints}
              currentUser={currentUser}
              getAvailableScreeningPoints={getAvailableScreeningPoints}
            />
          )}
        </Tab.Content>
      )}
    </div>
  );
}

export default ScreeningAccessManagement;
